import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  addDefaultCircles,
  diagramZoom,
  getDragBounds,
  highlightDiagram,
  isValidZoom
} from './utils';
import { PartDetailsAndSchematicResponse } from './types';
import { CatSkeleton } from 'components/blocks-components';
import { useGesture } from '@use-gesture/react';
import ZoomButtons from './zoom-buttons';
import { START_ZOOM } from './constants';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import cx from 'classnames';
import styles from './parts-diagram.module.scss';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { GA_PDP_DIAGRAM } from 'constants/analyticsConstants';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { CartResponse } from 'services/cart/types';
export interface PartsDiagramProps {
  hoveredItem: string | null;
  partDetailsAndSchematicsData: PartDetailsAndSchematicResponse;
  svgContent: string;
  isLoading: boolean;
  isZoomEnabled?: boolean;
  handleOnHover?: (refNumber: string | null) => void;
  isMobile?: boolean;
  cartDetails?: CartResponse;
}

const PartsDiagram: FC<PartsDiagramProps> = ({
  hoveredItem,
  svgContent,
  isLoading,
  partDetailsAndSchematicsData,
  isZoomEnabled = false,
  handleOnHover,
  isMobile = false,
  cartDetails
}) => {
  const containerRef = useRef(null);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const [zoom, setZoom] = useState(START_ZOOM);
  const [dragging, setDragging] = useState(false);
  const [dragBounds, setDragBounds] = useState(undefined);
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();

  const handleCircleSelection = useCallback(
    (partItem) => () => {
      if (isMobile && isMD === false) {
        handleOnHover(
          hoveredItem === partItem.referenceNumber
            ? null
            : partItem.referenceNumber
        );

        const gaItem: Product = {
          ...partItem,
          imageURL: '',
          type: ''
        };
        const diagramViewItemList = {
          item_list_id: GA_PDP_DIAGRAM,
          item_list_name: GA_PDP_DIAGRAM
        };
        const diagramCartPayload = {
          products: [...[], gaItem],
          itemListData: diagramViewItemList,
          cartItemsData: cartDetails,
          cartId: cartDetails?.orderHeader?.orderId,
          isProductsData: true,
          isSearchPageEvent: true
        };

        fireViewItemListEvent(diagramCartPayload);
      }
    },
    [
      handleOnHover,
      fireViewItemListEvent,
      hoveredItem,
      isMD,
      isMobile,
      cartDetails
    ]
  );
  const handleZoom = (newZoom: number) => {
    if (isValidZoom(newZoom)) {
      diagramZoom(containerRef.current, newZoom);
      setZoom(newZoom);

      const height = containerRef.current.clientHeight;
      const width = containerRef.current.clientWidth;
      const bounds = getDragBounds(height, width, newZoom);
      setDragBounds(bounds);
    }
  };

  const handleDrag = (event) => {
    const shouldDragDiagram = zoom > 1;
    if (shouldDragDiagram) {
      const [offsetX, offsetY] = event.offset;
      containerRef.current.style.transform = `scale3d(${zoom}, ${zoom}, ${zoom}) translate3d(${offsetX}px, ${offsetY}px, 0)`;
    }
  };

  const handlePinch = (state) => {
    const [d] = state.offset;
    const newZoom = d / 100;

    const transform = `scale3d(${newZoom}, ${newZoom}, ${newZoom})`;
    containerRef.current.style.transform = transform;
    return newZoom;
  };

  const handlePinchEnd = ({ memo = 1 }) => {
    handleZoom(memo);
  };

  useEffect(() => {
    if (containerRef.current) {
      if (hoveredItem?.trim()) {
        highlightDiagram(containerRef.current, hoveredItem);
      }
      partDetailsAndSchematicsData?.lineItems.map((partItem) => {
        if (partItem.referenceNumber?.trim()) {
          addDefaultCircles(
            containerRef.current,
            partItem.referenceNumber,
            handleCircleSelection(partItem)
          );
        }
      });
    }
  }, [
    hoveredItem,
    partDetailsAndSchematicsData?.lineItems,
    svgContent,
    zoom,
    dragging,
    handleCircleSelection
  ]);

  useGesture(
    {
      onDrag: (state) => handleDrag(state),
      onDragEnd: () => setDragging(false),
      onDragStart: () => setDragging(true),
      onPinch: (state) => handlePinch(state),
      onPinchEnd: (state) => handlePinchEnd(state)
    },
    {
      target: containerRef,
      eventOptions: { passive: false },
      drag: { bounds: dragBounds },
      pinch: {
        scaleBounds: { min: 100, max: 1500 }
      }
    }
  );

  return (
    <div
      className="overflow-hidden position-relative"
      style={{
        border: '1px solid var(--cat-theme-color-border-default)',
        borderRadius: 'var(--cat-border-radius-lg)'
      }}
    >
      {isLoading ? (
        <CatSkeleton />
      ) : (
        <>
          <div
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: svgContent || '' }}
            className={cx({
              [styles['parts-diagram-wrapper']]: isMobile,
              [styles['parts-diagram']]: isZoomEnabled,
              [styles['dragging']]: dragging
            })}
            data-testid="partsDiagram"
          />
          <Conditional test={isZoomEnabled}>
            <ZoomButtons handleZoom={handleZoom} zoom={zoom} />
          </Conditional>
        </>
      )}
    </div>
  );
};

export default PartsDiagram;
